import { useEffect, useState, type JSX } from 'react'
import { Dialog } from 'primereact/dialog'
import { api } from '../../../api/client.js'
import { useToast } from '../../system/ToastProvider.js'
import type { ConnectedAccount } from '../../../pages/admin/channels-types.js'
import { formatDateTime } from '../../../utils/formatDate.js'

export interface AccountDetail {
  id: string
  channel: string
  provider: string
  accountKey: string
  displayName: string
  connectedUser: string | null
  authType: 'oauth' | 'manual'
  status: 'connected' | 'expired' | 'revoked'
  lastTokenRefresh: string | null
  createdAt: string
  updatedAt: string
  maskedFields: Record<string, string> | null
}

export interface ViewAccountModalProps {
  visible: boolean
  provider: string
  account: ConnectedAccount | null
  onClose: () => void
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return formatDateTime(iso)
}

function labelize(key: string): string {
  return key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase())
}

/** Read-only details view for a connected account, including masked (never raw) manual-credential fields. */
export default function ViewAccountModal(props: ViewAccountModalProps): JSX.Element {
  const toast = useToast()
  const [detail, setDetail] = useState<AccountDetail | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!props.visible || !props.account) {
      setDetail(null)
      return
    }
    setLoading(true)
    api
      .get<AccountDetail>(`/admin/channels/${props.provider}/${encodeURIComponent(props.account.accountKey)}/details`)
      .then(setDetail)
      .catch((e) => {
        toast.error('Failed to load account details', e instanceof Error ? e.message : 'Failed to load account details.')
        props.onClose()
      })
      .finally(() => setLoading(false))
  }, [props.visible, props.account?.accountKey])

  return (
    <Dialog
      visible={props.visible}
      onHide={props.onClose}
      header={props.account ? `View ${props.account.displayName}` : 'View Account'}
      style={{ width: '32rem' }}
      modal
    >
      {loading || !detail ? (
        <div className="flex items-center justify-center py-8 text-sm text-gray-500">
          <i className="pi pi-spinner pi-spin mr-2" aria-hidden="true" />
          Loading…
        </div>
      ) : (
        <div className="space-y-4 text-sm">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Account Name</dt>
              <dd className="mt-0.5 text-gray-900">{detail.displayName}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Provider</dt>
              <dd className="mt-0.5 capitalize text-gray-900">{detail.provider}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Status</dt>
              <dd className="mt-0.5 capitalize text-gray-900">{detail.status}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Auth Type</dt>
              <dd className="mt-0.5 capitalize text-gray-900">{detail.authType}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Connected User</dt>
              <dd className="mt-0.5 text-gray-900">{detail.connectedUser ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Last Token Refresh</dt>
              <dd className="mt-0.5 text-gray-900">{formatDate(detail.lastTokenRefresh)}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Created</dt>
              <dd className="mt-0.5 text-gray-900">{formatDate(detail.createdAt)}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Updated</dt>
              <dd className="mt-0.5 text-gray-900">{formatDate(detail.updatedAt)}</dd>
            </div>
          </dl>

          {detail.maskedFields && Object.keys(detail.maskedFields).length > 0 && (
            <div className="border-t border-gray-100 pt-3">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">Credential Fields</p>
              <dl className="space-y-2">
                {Object.entries(detail.maskedFields).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between">
                    <dt className="text-gray-600">{labelize(key)}</dt>
                    <dd className="font-mono text-gray-900">{value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </div>
      )}
    </Dialog>
  )
}
